import { Component, OnInit } from '@angular/core';
import { LoaderService } from 'src/app/loader.service';
import { AUTHZ_FEATURES } from 'src/app/constants/permissions';
import { AuthzAccessHintService } from 'src/app/shared/authz-access-hint/authz-access-hint.service';
import { SharedService } from 'src/app/shared/shared.service';
import { Pagination, Table } from 'src/app/shared/types';
import { Toaster, ToasterType } from 'src/app/shared/types/toaster.types';
import { ToasterService } from 'src/app/toaster.service';

@Component({
  selector: 'app-limit-store-list',
  templateUrl: './limit-store-list.component.html',
  styleUrls: ['./limit-store-list.component.css']
})
export class LimitStoreListComponent implements OnInit {
  public table: Table = new Table({
    headers: {
      city: 'City',
      identifier: 'Identifier',
      identifierId: 'Name',
      limitType: 'Limit type',
      limit: 'Limit',
      warning: 'Warning',
      priority: 'Priority',
      active: 'Active'
    },
    pagination: new Pagination(0, 15, 0, [15, 20, 50]),
    sortableColumnKeys: ['city', 'identifier', 'identifierId', 'limitType', 'limit', 'priority', 'active'],
    data: []
  });

  public showFilters = true;
  public searchText = '';
  public filterComponents: any[] = [];
  public authzAccessHint: { message: string; requestUrl: string } | null = null;

  private cities: { id: string, name: string }[] = [];
  private cityNameToId: { [name: string]: string } = {};
  private cityIdToName: { [id: string]: string } = {};
  private serviceIdToName: { [id: string]: string } = {};
  private shifts: string[] = [];
  private services: { id: string, name: string }[] = [];
  private allLimits: any[] = [];
  private filteredLimits: any[] = [];
  private appliedFilters: any = {};
  private sortKey = '';

  private citiesFilter: any = {
    type: 'searchable-dropdown',
    title: 'City',
    values: [],
    default: [],
    key: 'limit-city'
  };
  private identifierFilter: any = {
    type: 'radio-button',
    title: 'Scope',
    name: 'limit-identifier',
    values: ['All', 'CITY', 'SHIFT', 'SERVICE'],
    default: 'All',
    key: 'limit-identifier'
  };
  private limitTypeFilter: any = {
    type: 'radio-button',
    title: 'Limit type',
    name: 'limit-type',
    values: ['All', 'NEGATIVE_BALANCE', 'NEGATIVE_BALANCE_WARN', 'NEGATIVE_DUE_BALANCE', 'NEGATIVE_DUE_BALANCE_WARN'],
    default: 'All',
    key: 'limit-type'
  };
  private activeFilter: any = {
    type: 'radio-button',
    title: 'Status',
    name: 'limit-active',
    values: ['All', 'Active', 'Inactive'],
    default: 'All',
    key: 'limit-active'
  };
  private shiftsFilter: any = {
    type: 'searchable-dropdown',
    title: 'Shift',
    values: [],
    default: [],
    key: 'limit-shift'
  };
  private servicesFilter: any = {
    type: 'searchable-dropdown',
    title: 'Service',
    values: [],
    default: [],
    key: 'limit-service'
  };

  constructor(
    private sharedService: SharedService,
    private loader: LoaderService,
    public toastService: ToasterService,
    private authzAccessHintService: AuthzAccessHintService
  ) {}

  ngOnInit() {
    this.loadAccessHint();
    this.refreshFilterComponents();
    this.fetchCities();
  }

  private async loadAccessHint(): Promise<void> {
    this.authzAccessHint = await this.authzAccessHintService.getAccessHintData(AUTHZ_FEATURES.LIMIT_STORE);
  }

  public onFilterChange(evt: any) {
    if (evt && evt['limit-city']) {
      this.loadCityDependents(evt['limit-city']);
    }
  }

  public applyFilters(filters: any) {
    this.appliedFilters = filters || {};
    this.table.pagination.pageNo = 0;
    this.fetchLimits();
  }

  public applySearch() {
    this.table.pagination.pageNo = 0;
    this.filterAndRender();
  }

  public renderPage() {
    this.renderTable();
  }

  public sortByHeader(header: string) {
    this.sortKey = header || '';
    this.filterAndRender();
  }

  private fetchCities() {
    this.loader.openLoading();
    this.sharedService.fetchCities().toPromise()
      .then((res: any) => {
        this.cities = ((res && res.data && res.data.cities) || [])
          .filter((city: any) => city.active !== false)
          .map((city: any) => ({ id: city._id, name: city.displayName }));
        this.cityNameToId = {};
        this.cityIdToName = {};
        this.cities.forEach((city) => {
          this.cityNameToId[city.name] = city.id;
          this.cityIdToName[city.id] = city.name;
        });
        this.citiesFilter = Object.assign({}, this.citiesFilter, {
          values: this.cities.map((city) => city.name)
        });
        this.refreshFilterComponents();
      })
      .catch(() => {
        this.toastService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: 'Error fetching cities'
        }));
      })
      .finally(() => this.loader.closeLoading());
  }

  private loadCityDependents(cityNames: string[]) {
    const names = (Array.isArray(cityNames) ? cityNames : []).filter(Boolean);
    if (!names.length) {
      this.shifts = [];
      this.services = [];
      this.serviceIdToName = {};
      this.patchShiftServiceFilters();
      return;
    }
    Promise.all(names.map((name) => {
      const cityId = this.cityNameToId[name];
      const shiftsPromise = this.sharedService.fetchShifts(name).toPromise()
        .then((res: any) => {
          const data: string[] = Array.isArray(res) ? res : (res && res.data ? res.data : []);
          return (data || []).filter(Boolean);
        })
        .catch(() => []);
      const servicesPromise = cityId
        ? this.sharedService.cityService(cityId).toPromise()
          .then((res: any) => ((res && res.data) || [])
            .filter((sd: any) => sd && sd.active !== false && sd.service)
            .map((sd: any) => ({ id: sd.service._id, name: sd.service.name })))
          .catch(() => [])
        : Promise.resolve([]);
      return Promise.all([shiftsPromise, servicesPromise]);
    })).then((results) => {
      const shiftSeen = {};
      const shifts = [];
      const serviceSeen = {};
      const services = [];
      results.forEach(([cityShifts, cityServices]) => {
        (cityShifts || []).forEach((shift) => {
          if (!shiftSeen[shift]) {
            shiftSeen[shift] = true;
            shifts.push(shift);
          }
        });
        (cityServices || []).forEach((svc) => {
          if (svc.id && !serviceSeen[svc.id]) {
            serviceSeen[svc.id] = true;
            services.push(svc);
          }
        });
      });
      this.shifts = shifts;
      this.services = services;
      this.serviceIdToName = {};
      services.forEach((svc) => {
        this.serviceIdToName[svc.id] = svc.name;
      });
      this.patchShiftServiceFilters();
      this.filterAndRender();
    });
  }

  private patchShiftServiceFilters() {
    this.shiftsFilter = Object.assign({}, this.shiftsFilter, { values: this.shifts.slice() });
    this.servicesFilter = Object.assign({}, this.servicesFilter, {
      values: this.services.map((svc) => svc.name)
    });
    this.refreshFilterComponents();
  }

  private refreshFilterComponents() {
    this.filterComponents = [
      this.citiesFilter,
      this.identifierFilter,
      this.limitTypeFilter,
      this.activeFilter,
      this.shiftsFilter,
      this.servicesFilter
    ];
  }

  private selectedCityIds(): string[] {
    const names = this.appliedFilters['limit-city'] || [];
    return (Array.isArray(names) ? names : [])
      .map((name: string) => this.cityNameToId[name])
      .filter(Boolean);
  }

  private fetchLimits() {
    const cityIds = this.selectedCityIds();
    if (!cityIds.length) {
      this.toastService.showToaster(new Toaster({
        type: ToasterType.WARNING,
        message: 'Select a city to list limits'
      }));
      this.showFilters = true;
      return;
    }

    const queries = this.buildQueries(cityIds);
    if (!queries.length) {
      this.allLimits = [];
      this.filterAndRender();
      return;
    }

    this.loader.openLoading();
    Promise.all(queries.map((query) => this.sharedService.fetchLimits(query).toPromise()))
      .then((responses) => {
        const merged = [];
        (responses || []).forEach((res: any) => {
          const rows = Array.isArray(res) ? res : (res && res.data ? res.data : []);
          (rows || []).forEach((row) => merged.push(row));
        });
        this.allLimits = this.dedupeLimits(merged);
        this.filterAndRender();
      })
      .catch((error) => {
        this.allLimits = [];
        this.filterAndRender();
        const body = error && error.error;
        this.toastService.showToaster(new Toaster({
          type: ToasterType.WARNING,
          message: (body && body.message) || 'Error fetching limits'
        }));
      })
      .finally(() => this.loader.closeLoading());
  }

  private buildQueries(cityIds: string[]): any[] {
    const identifier = this.appliedFilters['limit-identifier'] || 'All';
    const selectedShifts: string[] = this.appliedFilters['limit-shift'] || [];
    const selectedServiceNames: string[] = this.appliedFilters['limit-service'] || [];
    const selectedServiceIds = this.services
      .filter((svc) => selectedServiceNames.indexOf(svc.name) >= 0)
      .map((svc) => svc.id);
    const queries = [];

    const includeCity = identifier === 'All' || identifier === 'CITY';
    const includeShift = identifier === 'All' || identifier === 'SHIFT';
    const includeService = identifier === 'All' || identifier === 'SERVICE';

    if (includeCity) {
      queries.push({ identifier: 'CITY', identifierIds: cityIds });
    }
    if (includeShift) {
      const shiftIds = selectedShifts.length ? selectedShifts : this.shifts;
      if (shiftIds.length) {
        queries.push({ identifier: 'SHIFT', identifierIds: shiftIds });
      }
    }
    if (includeService) {
      const serviceIds = selectedServiceIds.length ? selectedServiceIds : this.services.map((svc) => svc.id);
      if (serviceIds.length) {
        queries.push({ identifier: 'SERVICE', identifierIds: serviceIds });
      }
    }
    return queries;
  }

  private dedupeLimits(rows: any[]): any[] {
    const seen = {};
    return (rows || []).filter((row) => {
      const key = [
        row.identifier,
        row.identifierId,
        row.limitType,
        row.city,
        row.limit,
        row.priority
      ].join('|');
      if (seen[key]) {
        return false;
      }
      seen[key] = true;
      return true;
    });
  }

  private filterAndRender() {
    const limitType = this.appliedFilters['limit-type'] || 'All';
    const active = this.appliedFilters['limit-active'] || 'All';
    const search = (this.searchText || '').trim().toLowerCase();
    const cityIds = this.selectedCityIds();

    this.filteredLimits = this.allLimits.filter((row) => {
      if (cityIds.length && cityIds.indexOf(row.city) < 0) {
        return false;
      }
      if (limitType !== 'All' && row.limitType !== limitType) {
        return false;
      }
      if (active === 'Active' && !row.active) {
        return false;
      }
      if (active === 'Inactive' && row.active) {
        return false;
      }
      if (search) {
        const haystack = [
          row.identifier,
          row.identifierId,
          this.identifierName(row),
          row.limitType,
          row.limit,
          row.city,
          this.cityIdToName[row.city],
          row.metadata && row.metadata.negativeBalanceWarn
        ].join(' ').toLowerCase();
        if (haystack.indexOf(search) < 0) {
          return false;
        }
      }
      return true;
    });

    if (this.sortKey) {
      const desc = this.sortKey.charAt(0) === '-';
      const key = desc ? this.sortKey.slice(1) : this.sortKey;
      this.filteredLimits.sort((a, b) => {
        const av = this.sortValue(a, key);
        const bv = this.sortValue(b, key);
        if (av === bv) {
          return 0;
        }
        if (av > bv) {
          return desc ? -1 : 1;
        }
        return desc ? 1 : -1;
      });
    }

    this.table.pagination.count = this.filteredLimits.length;
    this.renderTable();
  }

  private sortValue(row: any, key: string): any {
    if (key === 'city') {
      return (this.cityIdToName[row.city] || row.city || '').toString().toLowerCase();
    }
    if (key === 'identifierId') {
      return this.identifierName(row).toLowerCase();
    }
    if (key === 'warning') {
      return row.metadata && row.metadata.negativeBalanceWarn;
    }
    const value = row[key];
    return value == null ? '' : value;
  }

  private renderTable() {
    const pageNo = this.table.pagination.pageNo || 0;
    const perPage = this.table.pagination.perPage || 15;
    const start = pageNo * perPage;
    const pageRows = this.filteredLimits.slice(start, start + perPage);
    this.table.data = pageRows.map((row) => ({
      identifier: row.identifier || '--',
      identifierId: this.identifierName(row),
      limitType: row.limitType || '--',
      limit: row.limit != null ? String(row.limit) : '--',
      warning: row.metadata && row.metadata.negativeBalanceWarn != null
        ? String(row.metadata.negativeBalanceWarn)
        : '--',
      city: this.cityIdToName[row.city] || row.city || '--',
      priority: row.priority != null ? String(row.priority) : '--',
      active: row.active ? 'Active' : 'Inactive'
    }));
  }

  private identifierName(row: any): string {
    const id = row && row.identifierId;
    if (!id) {
      return '--';
    }
    if (row.identifier === 'CITY') {
      return this.cityIdToName[id] || '--';
    }
    if (row.identifier === 'SERVICE') {
      return this.serviceIdToName[id] || '--';
    }
    return String(id);
  }
}
